/**
 * OrejaReviewPhase - Review screen with editable task proposals.
 *
 * Displays updated tasks and new tasks as selectable, editable cards with
 * doc quality scores, field change diffs, and commit/refine action buttons.
 *
 * @module OrejaReviewPhase
 */

import { useState, useCallback, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, RefreshCw, Check, Upload, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { ConfirmDialog } from '@/components/shared/DialogWrapper';
import { HierarchyExplainerButton } from '@/components/shared/HierarchyExplainerModal';
import { OrejaUpdatedTaskCard } from './OrejaUpdatedTaskCard';
import { OrejaNewTaskCard } from './OrejaNewTaskCard';
import type { OrejaNewTaskProposal } from '@/lib/oreja/types';
import { useAnalysisOptions } from '@/components/analysis/hooks/useAnalysisOptions';
import type { OrejaProposals } from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Existing task data for displaying current descriptions in diffs. */
export interface ExistingTaskInfo {
  clickup_task_id: string;
  name: string;
  description: string;
  status_name: string;
}

export interface OrejaReviewPhaseProps {
  /** All proposals from the AI. */
  proposals: OrejaProposals;
  /** Existing tasks in the ClickUp list (for diff display). */
  existingTasks: ExistingTaskInfo[];
  /** ClickUp list ID for fetching status/priority options. */
  clickupListId?: string | null;
  /** Callback when a proposal field is edited. */
  onProposalEdit: (type: 'updated' | 'new', index: number, field: string, value: string) => void;
  /** Callback to edit a single field change "to" value (replaces JSON serialization). */
  onEditFieldChange?: (index: number, fieldChangeIndex: number, newToValue: string) => void;
  /** Callback when a proposal is toggled on/off. */
  onProposalToggle: (type: 'updated' | 'new', index: number, selected: boolean) => void;
  /** Callback to refine proposals with additional input. */
  onRefine: () => void;
  /** Callback to commit selected proposals to ClickUp. */
  onCommit: (selectedUpdated: number[], selectedNew: number[]) => void;
  /** Callback to add a blank new task to proposals. */
  onAddTask?: (task: OrejaNewTaskProposal) => void;
  /** Whether commit is in progress. */
  isCommitting?: boolean;
  /** Whether refinement is in progress. */
  isRefining?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Review phase: displays all proposals with selection, editing, and commit controls.
 *
 * @param props - Component props
 * @returns The review phase UI with task cards and action buttons
 */
export function OrejaReviewPhase({
  proposals,
  existingTasks,
  clickupListId,
  onProposalEdit,
  onEditFieldChange,
  onProposalToggle,
  onRefine,
  onCommit,
  onAddTask,
  isCommitting = false,
  isRefining = false,
}: OrejaReviewPhaseProps) {
  // Fetch real ClickUp statuses and priorities for this list
  const { priorityOptions, statusOptions } = useAnalysisOptions(clickupListId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updatedExpanded, setUpdatedExpanded] = useState(true);
  const [newExpanded, setNewExpanded] = useState(true);
  // Selection state: default all updated to selected, new to unselected
  const [updatedSelection, setUpdatedSelection] = useState<boolean[]>(
    () => proposals.updated_tasks.map(() => true),
  );
  const [newSelection, setNewSelection] = useState<boolean[]>(
    () => proposals.new_tasks.map(() => true),
  );

  const handleUpdatedToggle = useCallback((index: number, selected: boolean) => {
    setUpdatedSelection((prev) => {
      const next = [...prev];
      next[index] = selected;
      return next;
    });
    onProposalToggle('updated', index, selected);
  }, [onProposalToggle]);

  const handleNewToggle = useCallback((index: number, selected: boolean) => {
    setNewSelection((prev) => {
      const next = [...prev];
      next[index] = selected;
      return next;
    });
    onProposalToggle('new', index, selected);
  }, [onProposalToggle]);

  const handleUpdatedRemove = useCallback((index: number) => {
    handleUpdatedToggle(index, false);
  }, [handleUpdatedToggle]);

  const handleNewRemove = useCallback((index: number) => {
    handleNewToggle(index, false);
  }, [handleNewToggle]);

  const allUpdatedSelected = updatedSelection.length > 0 && updatedSelection.every(Boolean);
  const allNewSelected = newSelection.length > 0 && newSelection.every(Boolean);

  const handleToggleAllUpdated = useCallback(() => {
    const selectAll = !allUpdatedSelected;
    setUpdatedSelection(proposals.updated_tasks.map(() => selectAll));
    proposals.updated_tasks.forEach((_, idx) => onProposalToggle('updated', idx, selectAll));
  }, [allUpdatedSelected, proposals.updated_tasks, onProposalToggle]);

  const handleToggleAllNew = useCallback(() => {
    const selectAll = !allNewSelected;
    setNewSelection(proposals.new_tasks.map(() => selectAll));
    proposals.new_tasks.forEach((_, idx) => onProposalToggle('new', idx, selectAll));
  }, [allNewSelected, proposals.new_tasks, onProposalToggle]);

  const handleAddTask = useCallback(() => {
    if (!onAddTask) return;
    const blank: OrejaNewTaskProposal = {
      title: 'New Task',
      description: '',
      confidence: 1.0,
      reasoning: 'Manually added by user',
    };
    onAddTask(blank);
    // Select the newly added task
    setNewSelection((prev) => [...prev, true]);
  }, [onAddTask]);

  /** Find existing task description for diff display. */
  const getExistingDescription = useCallback((taskId: string): string => {
    const task = existingTasks.find((t) => t.clickup_task_id === taskId);
    return task?.description ?? '';
  }, [existingTasks]);

  const selectedUpdatedIndexes = useMemo(
    () => updatedSelection.reduce<number[]>((acc, sel, idx) => sel ? [...acc, idx] : acc, []),
    [updatedSelection],
  );
  const selectedNewIndexes = useMemo(
    () => newSelection.reduce<number[]>((acc, sel, idx) => sel ? [...acc, idx] : acc, []),
    [newSelection],
  );

  const totalSelected = selectedUpdatedIndexes.length + selectedNewIndexes.length;
  const isBusy = isCommitting || isRefining;

  return (
    <div className="flex flex-col h-full">
      {/* Summary */}
      {proposals.summary && (
        <div className="px-4 pt-3 pb-1">
          <p className="text-sm text-muted-foreground">{proposals.summary}</p>
        </div>
      )}

      <ScrollArea className="flex-1 px-4">
        <div className="space-y-4 py-3">
          {/* Updated tasks section */}
          {proposals.updated_tasks.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setUpdatedExpanded((prev) => !prev)}
                  className="flex items-center gap-1.5 group"
                  aria-expanded={updatedExpanded}
                  aria-label="Toggle Updated Tasks"
                >
                  {updatedExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <h3 className="text-sm font-semibold uppercase tracking-wide">Updated Tasks</h3>
                </button>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  {proposals.updated_tasks.length}
                </Badge>
                {!updatedExpanded && selectedUpdatedIndexes.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {selectedUpdatedIndexes.length} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleToggleAllUpdated}
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {allUpdatedSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {updatedExpanded && proposals.updated_tasks.map((task, idx) => (
                <OrejaUpdatedTaskCard
                  key={task.clickup_task_id}
                  proposal={task}
                  index={idx}
                  selected={updatedSelection[idx] ?? false}
                  currentDescription={getExistingDescription(task.clickup_task_id)}
                  onToggle={handleUpdatedToggle}
                  onEdit={(i, field, val) => onProposalEdit('updated', i, field, val)}
                  onEditFieldChange={onEditFieldChange}
                  onRemove={handleUpdatedRemove}
                />
              ))}
            </div>
          )}

          {/* New tasks section */}
          {proposals.new_tasks.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNewExpanded((prev) => !prev)}
                  className="flex items-center gap-1.5 group"
                  aria-expanded={newExpanded}
                  aria-label="Toggle New Tasks"
                >
                  {newExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <h3 className="text-sm font-semibold uppercase tracking-wide">New Tasks</h3>
                </button>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  {proposals.new_tasks.length}
                </Badge>
                <HierarchyExplainerButton />
                {!newExpanded && selectedNewIndexes.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {selectedNewIndexes.length} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleToggleAllNew}
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {allNewSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {newExpanded && proposals.new_tasks.map((task, idx) => (
                <OrejaNewTaskCard
                  key={`new-${idx}`}
                  proposal={task}
                  index={idx}
                  selected={newSelection[idx] ?? false}
                  priorityOptions={priorityOptions}
                  statusOptions={statusOptions}
                  onToggle={handleNewToggle}
                  onEdit={(i, field, val) => onProposalEdit('new', i, field, val)}
                  onRemove={handleNewRemove}
                  parentTitle={task.parent_task_id
                    ? existingTasks.find(t => t.clickup_task_id === task.parent_task_id)?.name ?? null
                    : null}
                />
              ))}
            </div>
          )}

          {/* Add task button */}
          {onAddTask && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddTask}
              className="w-full"
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              Add Task Manually
            </Button>
          )}
        </div>
      </ScrollArea>

      {/* Action buttons */}
      <div className="flex items-center gap-2 border-t px-4 py-3">
        <Button
          variant="outline"
          onClick={onRefine}
          disabled={isBusy}
          className="flex-1"
        >
          {isRefining ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refine
        </Button>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={isBusy || totalSelected === 0}
          className="flex-1"
        >
          {isCommitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Commit {totalSelected} change{totalSelected !== 1 ? 's' : ''}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Push changes to ClickUp?"
        description={`This will push ${totalSelected} change${totalSelected !== 1 ? 's' : ''} to ClickUp${selectedUpdatedIndexes.length > 0 && selectedNewIndexes.length > 0 ? ` (${selectedUpdatedIndexes.length} update${selectedUpdatedIndexes.length !== 1 ? 's' : ''}, ${selectedNewIndexes.length} new task${selectedNewIndexes.length !== 1 ? 's' : ''})` : selectedUpdatedIndexes.length > 0 ? ` (${selectedUpdatedIndexes.length} update${selectedUpdatedIndexes.length !== 1 ? 's' : ''})` : ` (${selectedNewIndexes.length} new task${selectedNewIndexes.length !== 1 ? 's' : ''})`}.`}
        titleIcon={Upload}
        onConfirm={() => onCommit(selectedUpdatedIndexes, selectedNewIndexes)}
        confirmLabel="Push to ClickUp"
        isLoading={isCommitting}
      />
    </div>
  );
}
